import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { dataService } from '../../services/dataService'
import { Project } from '../../types'
import { toDateOnly } from '../../utils/validators'
import { Modal } from '../ui/Modal'
import { Badge, getStatusVariant } from '../ui/Badge'
import { Plus, EditIcon, Trash2 } from '../icons/Icons'
import { useVersionControl } from '../../hooks/useVersionControl'
import { ConflictModal } from '../ui/ConflictModal'
import { BulkUploadModal } from '../shared/BulkUploadModal'
import s from './shared.module.css'

function generateProjectId(customer: string, type: string, currency: string): string {
  const prefix = currency === 'GBP' ? 'NSUK' : currency === 'INR' ? 'NSPL' : 'NSI'
  const custAbbrev = customer.split(' ').map(w => w.substring(0, 2).toUpperCase()).join('').substring(0, 8) || 'CUST'
  const typeAbbrev = type === 'Implementation' ? 'IMPL' : type === 'Support' ? 'SUPP' : 'TM'
  const year = new Date().getFullYear()
  const rand = Math.random().toString(36).substring(2, 10).toUpperCase()
  return `${prefix}-${custAbbrev}-${typeAbbrev}-${year}-${rand}`
}

const STATUSES = ['Active', 'On Hold', 'Closed', 'Tentative']
const TYPES = ['Implementation', 'Support', 'T&M']

export default function Projects() {
  const { organization, user, orgMember } = useAuth()
  const { projects, allProjects, setProjects, resetProjects, customers, partners, partnerRateSheets, refreshData } = useAppData()
  const orgId = organization!.id

  const canBulkUpload = orgMember?.user_type === 'owner' || orgMember?.user_type === 'admin' || orgMember?.user_type === 'manager'

  const [showModal, setShowModal] = useState(false)
  const [showBulkUpload, setShowBulkUpload] = useState(false)
  const [editing, setEditing] = useState<Project | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [filters, setFilters] = useState({ name: '', status: '', customerId: '' })
  const [form, setForm] = useState({
    title: '', description: '', customer_id: '', start_date: '', end_date: '',
    currency: 'USD' as Project['currency'], project_type: 'Implementation' as Project['project_type'],
    status: 'Active' as Project['status'], budget: '', department: '', technology: '',
    partner_id: '', partner_rate_sheet_id: '', project_id: ''
  })

  const {
    saveWithVersionCheck,
    activeEditors,
    conflict,
    clearConflict
  } = useVersionControl<Project & { version: number }>({
    tableName: 'projects',
    recordId: editing?.id || null
  })

  const openAdd = () => {
    setEditing(null)
    setForm({ title: '', description: '', customer_id: '', start_date: '', end_date: '', currency: 'USD', project_type: 'Implementation', status: 'Active', budget: '', department: '', technology: '', partner_id: '', partner_rate_sheet_id: '', project_id: '' })
    setSaveError(''); setShowModal(true)
  }
  const openEdit = (p: Project) => {
    setEditing(p)
    setForm({ title: p.title, description: p.description, customer_id: p.customer_id || '', start_date: toDateOnly(p.start_date), end_date: toDateOnly(p.end_date), currency: p.currency, project_type: p.project_type, status: p.status, budget: p.budget?.toString() || '', department: p.department || '', technology: p.technology || '', partner_id: p.partner_id || '', partner_rate_sheet_id: p.partner_rate_sheet_id || '', project_id: p.project_id })
    setSaveError(''); setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); if (saving) return; setSaving(true); setSaveError('')
    try {
      const customer = customers.find(c => c.id === form.customer_id)
      const payload = {
        ...form,
        project_id: form.project_id || generateProjectId(customer?.title || '', form.project_type, form.currency),
        budget: form.budget ? parseFloat(form.budget) : undefined,
        partner_id: form.partner_id || undefined,
        partner_rate_sheet_id: form.partner_rate_sheet_id || undefined,
      }

      if (editing) {
        const result = await saveWithVersionCheck(
          editing as Project & { version: number },
          payload,
          `Updated project: ${form.title}`
        )

        if (result.conflict) {
          setSaveError('Version conflict detected. Please review and resolve.')
          return
        }

        if (!result.success) {
          setSaveError('Failed to save. Please try again.')
          return
        }
      } else {
        await dataService.createProject(orgId, payload as any)
      }

      setShowModal(false); await refreshData()
    } catch (err: any) { setSaveError(err?.message || 'Failed to save. Please try again.') } finally { setSaving(false) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this project?')) return
    await dataService.deleteProject(id); await refreshData()
  }

  const applyFilters = () => {
    let result = allProjects
    if (filters.name) result = result.filter(p => p.title.toLowerCase().includes(filters.name.toLowerCase()))
    if (filters.status) result = result.filter(p => p.status === filters.status)
    if (filters.customerId) result = result.filter(p => p.customer_id === filters.customerId)
    setProjects(result)
  }

  const getCustomerName = (id: string) => customers.find(c => c.id === id)?.title || '—'
  const availableRateSheets = partnerRateSheets.filter(rs => rs.partner_id === form.partner_id)

  return (
    <div>
      <div className={s.pageHeader}>
        <div><h2 className={s.pageTitle}>Projects</h2><p className={s.pageSubtitle}>{allProjects.length} project{allProjects.length !== 1 ? 's' : ''}</p></div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canBulkUpload && <button className={s.secondaryBtn} onClick={() => setShowBulkUpload(true)}>Bulk Upload</button>}
          <button className={s.primaryBtn} onClick={openAdd}><Plus size={14} /> Add Project</button>
        </div>
      </div>

      <div className={s.card}>
        <div className={s.filterBar}>
          <input className={s.filterInput} placeholder="Search by name..." value={filters.name} onChange={e => setFilters(f => ({ ...f, name: e.target.value }))} />
          <select className={s.filterSelect} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">All Statuses</option>{STATUSES.map(st => <option key={st}>{st}</option>)}
          </select>
          <select className={s.filterSelect} value={filters.customerId} onChange={e => setFilters(f => ({ ...f, customerId: e.target.value }))}>
            <option value="">All Customers</option>{customers.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <button className={s.primaryBtn} onClick={applyFilters}>Filter</button>
          <button className={s.secondaryBtn} onClick={() => { setFilters({ name: '', status: '', customerId: '' }); resetProjects() }}>Reset</button>
        </div>

        <table className={s.table}>
          <thead><tr><th>Project</th><th>ID</th><th>Customer</th><th>Type</th><th>Status</th><th>Dates</th><th>Currency</th><th>Actions</th></tr></thead>
          <tbody>
            {projects.length === 0 && <tr><td colSpan={8} className={s.emptyState}>No projects found</td></tr>}
            {projects.map(p => (
              <tr key={p.id}>
                <td style={{ fontWeight: 600 }}>{p.title}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.project_id}</td>
                <td>{getCustomerName(p.customer_id || '')}</td>
                <td>{p.project_type}</td>
                <td><Badge variant={getStatusVariant(p.status)}>{p.status}</Badge></td>
                <td style={{ fontSize: 12 }}>{p.start_date} → {p.end_date}</td>
                <td>{p.currency}</td>
                <td><div className={s.actions}>
                  <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEdit(p)}><EditIcon size={14} /></button>
                  <button className={`${s.actionBtn} ${s.del}`} onClick={() => handleDelete(p.id)}><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showBulkUpload && (
        <BulkUploadModal
          entityType="projects"
          orgId={orgId}
          userId={user!.id}
          customers={customers}
          partners={partners}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => { setShowBulkUpload(false); refreshData() }}
        />
      )}

      <Modal open={showModal} title={editing ? 'Edit Project' : 'Add Project'} onClose={() => setShowModal(false)} maxWidth={600}
        footer={<><button className={s.secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button><button className={s.primaryBtn} form="projectForm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></>}>
        <form id="projectForm" onSubmit={handleSubmit}>
          {saveError && <div className={s.formError}>{saveError}</div>}
          {editing && activeEditors.length > 0 && (
            <div style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', fontSize: '14px' }}>
              <strong>⚠️ Currently being edited by:</strong> {activeEditors.filter(e => e.user_id !== user?.id).map(e => e.display_name).join(', ') || 'another user'}
            </div>
          )}
          <div className={s.formGroup}><label>Project Name *</label><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required /></div>
          <div className={s.formGroup}><label>Description</label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Customer</label>
              <select value={form.customer_id} onChange={e => setForm(f => ({ ...f, customer_id: e.target.value }))}>
                <option value="">Select customer...</option>{customers.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div className={s.formGroup}><label>Project Type *</label>
              <select value={form.project_type} onChange={e => setForm(f => ({ ...f, project_type: e.target.value as any }))} required>
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Start Date *</label><input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} required /></div>
            <div className={s.formGroup}><label>End Date *</label><input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} required /></div>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Status *</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as any }))} required>
                {STATUSES.map(st => <option key={st}>{st}</option>)}
              </select>
            </div>
            <div className={s.formGroup}><label>Currency *</label>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value as any }))}>
                <option value="USD">USD</option><option value="GBP">GBP</option><option value="INR">INR</option>
              </select>
            </div>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Budget</label><input type="number" step="0.01" min="0" value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} /></div>
            <div className={s.formGroup}><label>Department</label><input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} /></div>
          </div>
          <div className={s.formGroup}><label>Technology</label><input value={form.technology} onChange={e => setForm(f => ({ ...f, technology: e.target.value }))} /></div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Partner (Optional)</label>
              <select value={form.partner_id} onChange={e => setForm(f => ({ ...f, partner_id: e.target.value, partner_rate_sheet_id: '' }))}>
                <option value="">No partner</option>{partners.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            {form.partner_id && (
              <div className={s.formGroup}><label>Partner Rate Sheet</label>
                <select value={form.partner_rate_sheet_id} onChange={e => setForm(f => ({ ...f, partner_rate_sheet_id: e.target.value }))}>
                  <option value="">None</option>{availableRateSheets.map(rs => <option key={rs.id} value={rs.id}>{rs.title}</option>)}
                </select>
              </div>
            )}
          </div>
        </form>
      </Modal>

      {conflict && (
        <ConflictModal
          conflict={conflict}
          onReload={async () => {
            clearConflict()
            await refreshData()
            setSaving(false)
          }}
          onOverride={async () => {
            clearConflict()
            setSaving(false)
          }}
          onCancel={() => {
            clearConflict()
            setSaving(false)
          }}
        />
      )}
    </div>
  )
}
